import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import CreateCurrencyForm from './components/CreateCurrencyForm';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { getCurrencies, deleteCurrency, updateCurrency, createCurrency, getCurrencyRates } from './api/currencyApi';
import { useAuth } from '../../contexts/AuthProvider';

const MasterCurrency = () => {
  const navigate = useNavigate();
  const { can, capabilitiesLoading } = useAuth();
  const canCurrency = (key) => can(key, 'master-currency');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [todayRates, setTodayRates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTodayLoading, setIsTodayLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  useEffect(() => {
    loadCurrencies();
    loadTodayRates();
  }, []);

  const loadCurrencies = async () => {
    setIsLoading(true);
    try {
      const response = await getCurrencies();
      setCurrencies(Array.isArray(response) ? response : response.data || []);
    } catch (error) {
      console.error('Error loading currencies:', error);
      showAlert('danger', 'Failed to load currencies.');
      setCurrencies([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTodayRates = async () => {
    setIsTodayLoading(true);
    try {
      const response = await getCurrencyRates();
      const all = Array.isArray(response) ? response : response.data || [];
      const today = new Date().toISOString().slice(0, 10);
      setTodayRates(all.filter(r => r.start_date <= today && r.end_date >= today));
    } catch (error) {
      console.error('Error loading today rates:', error);
      setTodayRates([]);
    } finally {
      setIsTodayLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    setFilterData({ dateFrom: '', dateTo: '', client: '', project: '', status: '' });
  };

  const handleFilterApply = () => {};

  const todayRateMap = Object.fromEntries(todayRates.map(r => [String(r.currency_id), r]));

  const columns = [
    { key: 'currencyCode', label: 'Currency Code' },
    { key: 'currencyName', label: 'Currency Name' },
    { key: 'symbol', label: 'Symbol' },
    {
      key: 'id',
      label: "Today's Rate (IDR)",
      render: (value) => {
        const rate = todayRateMap[String(value)];
        return rate
          ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(rate.exchange_rate)
          : <span className="text-muted">—</span>;
      }
    },
    {
      key: 'isBaseCurrency',
      label: 'Base Currency',
      render: (value) => value
        ? <span className="badge badge-outline text-success">Yes</span>
        : <span className="badge badge-outline text-secondary">No</span>
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (value) => value
        ? <span className="badge badge-outline text-success">Active</span>
        : <span className="badge badge-outline text-danger">Inactive</span>
    },
    {
      key: 'decimalPlaces',
      label: 'Decimal Places',
      render: (value) => value ?? 2
    }
  ];

  const handleFormSubmit = async (formData) => {
    if (selectedCurrency && !canCurrency('edit')) {
      showAlert('danger', 'You do not have permission to edit currencies.');
      return;
    }
    if (!selectedCurrency && !canCurrency('create')) {
      showAlert('danger', 'You do not have permission to create currencies.');
      return;
    }
    try {
      if (selectedCurrency) {
        await updateCurrency(selectedCurrency.id, formData);
        showAlert('success', 'Currency updated successfully!');
      } else {
        await createCurrency(formData);
        showAlert('success', 'Currency created successfully!');
      }
      await loadCurrencies();
      setIsModalOpen(false);
      setSelectedCurrency(null);
    } catch (error) {
      showAlert('danger', error.response?.data?.message || 'Error saving currency');
    }
  };

  const handleEdit = (row) => {
    if (!canCurrency('edit')) { showAlert('danger', 'You do not have permission to edit currencies.'); return; }
    setSelectedCurrency(row); setIsModalOpen(true);
  };
  const handleDelete = (row) => {
    if (!canCurrency('delete')) { showAlert('danger', 'You do not have permission to delete currencies.'); return; }
    setItemToDelete(row); setShowDeleteModal(true);
  };
  const handleView = (row) => { navigate(`/master-currency/${row.id}`); };

  return (
    <div className="container-xl">
      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Currency Management</h3>
        </div>
        {canCurrency('create') && (
          <div className="col-auto ms-auto d-print-none">
            <div className="btn-list">
              <button
                className="btn btn-primary"
                onClick={() => { setSelectedCurrency(null); setIsModalOpen(true); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M12 5l0 14"></path>
                  <path d="M5 12l14 0"></path>
                </svg>
                Create
              </button>
            </div>
          </div>
        )}
      </div>

      <CreateCurrencyForm
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedCurrency(null); }}
        onSubmit={handleFormSubmit}
        initialData={selectedCurrency}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setItemToDelete(null); }}
        onConfirm={async () => {
          if (!itemToDelete?.id) return;
          setIsLoading(true);
          try {
            await deleteCurrency(itemToDelete.id);
            showAlert('success', 'Currency deleted successfully!');
            await loadCurrencies();
          } catch (error) {
            console.error('Error deleting currency:', error);
            showAlert('danger', 'Failed to delete currency.');
          } finally {
            setIsLoading(false);
          }
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        title="Delete Currency"
        message="Are you sure you want to delete this currency? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={currencies}
                      columns={columns}
                      onEdit={canCurrency('edit') ? handleEdit : undefined}
                      onDelete={canCurrency('delete') ? handleDelete : undefined}
                      onView={handleView}
                      isLoading={isLoading || capabilitiesLoading}
                      filterComponent={
                        <FilterDropdown
                          filterData={filterData}
                          onFilterChange={handleFilterChange}
                          onReset={handleFilterReset}
                          onApply={handleFilterApply}
                        />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search currencies..."
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={() => showAlert('info', 'Export to Excel functionality coming soon')}
                          onExportPDF={() => showAlert('info', 'Export to PDF functionality coming soon')}
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterCurrency;
